import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  findIdByName,
  findIndexById,
  normalizeSelectValue,
} from './selectAssignmentUtils';
import { useAssignmentLookup } from './useAssignmentLookup';

import { ExpertiseApi, PositionAPI } from 'services';
import { IPositionResponseNew } from 'services/position/position.interface';
import { IExpertise } from 'views/pages/ExpertiseManagement/expertise-management.interface';

interface IAssignmentInitialValues {
  expertiseId?: number;
  expertiseName?: string;
  positionId?: number;
  positionName?: string;
  targetPositionId?: number;
  targetPositionName?: string;
}

interface IUseExpertisePositionAssignmentProps {
  defaultValue: string;
  organizationId?: number;
  requestScope?: string;
  initialValues?: IAssignmentInitialValues;
  onExpertiseValueChange?: (value: string) => void;
}

interface IUseExpertisePositionAssignmentResult {
  expertises: IExpertise[];
  positions: IPositionResponseNew[];
  targetPositions: IPositionResponseNew[];
  expertiseValue: string;
  positionValue: string;
  targetPositionValue: string;
  optionsExpertise: string[];
  optionsPositions: string[];
  optionsTargetPositions: string[];
  selectedExpertiseId?: number;
  selectedPosition?: IPositionResponseNew;
  selectedPositionId?: number;
  selectedTargetPositionId?: number;
  isExpertisesLoading: boolean;
  isPositionsLoading: boolean;
  isAssignmentReady: boolean;
  hasExpertisesError: boolean;
  hasPositionsError: boolean;
  retryExpertises: () => void;
  retryPositions: () => void;
  onSelectChangeExpertise: (value: string) => void;
  onSelectChangePosition: (value: string) => void;
  onSelectChangeTargetPosition: (value: string) => void;
}

const FIRST_INDEX = 0;
const EMPTY_EXPERTISES: IExpertise[] = [];
const EMPTY_POSITIONS: IPositionResponseNew[] = [];

const sortPositions = (positions: IPositionResponseNew[]): IPositionResponseNew[] =>
  [...positions].sort(
    (firstPosition, secondPosition) => firstPosition.orderNo - secondPosition.orderNo,
  );

const useExpertisePositionAssignment = ({
  defaultValue,
  initialValues,
  organizationId,
  requestScope,
  onExpertiseValueChange,
}: IUseExpertisePositionAssignmentProps): IUseExpertisePositionAssignmentResult => {
  const initialExpertiseValue = normalizeSelectValue(
    initialValues?.expertiseName,
    defaultValue,
  );
  const initialPositionValue = normalizeSelectValue(
    initialValues?.positionName,
    defaultValue,
  );
  const initialTargetPositionValue = normalizeSelectValue(
    initialValues?.targetPositionName ?? initialValues?.positionName,
    defaultValue,
  );

  const [expertiseValue, setExpertiseValue] = useState(initialExpertiseValue);
  const [positionValue, setPositionValue] = useState(initialPositionValue);
  const [targetPositionValue, setTargetPositionValue] = useState(
    initialTargetPositionValue,
  );
  const positionValueRef = useRef(positionValue);
  const scope = organizationId ? requestScope ?? String(organizationId) : null;
  const readExpertises = useCallback(
    (signal: AbortSignal) =>
      new ExpertiseApi().getAllExpertisesByOrgId(organizationId!, { signal }),
    [organizationId],
  );
  const expertiseLookup = useAssignmentLookup(
    scope,
    readExpertises,
    'Assignment.expertises',
  );
  const expertises = expertiseLookup.data ?? EMPTY_EXPERTISES;
  const expertiseId =
    findIdByName(expertises, expertiseValue) ??
    (expertiseValue === initialExpertiseValue ? initialValues?.expertiseId : undefined);
  const readPositions = useCallback(
    async (signal: AbortSignal) =>
      sortPositions(
        await new PositionAPI().getAllPositionsByExpertise(expertiseId!, { signal }),
      ),
    [expertiseId],
  );
  const positionLookup = useAssignmentLookup(
    scope && expertiseId ? `${scope}:${expertiseId}` : null,
    readPositions,
    'Assignment.positions',
  );
  const positions = positionLookup.data ?? EMPTY_POSITIONS;
  const isExpertisesLoading = expertiseLookup.isLoading;
  const isPositionsLoading = positionLookup.isLoading;

  useEffect(() => {
    positionValueRef.current = positionValue;
  }, [positionValue]);

  const selectedPosition = useMemo(
    () => positions.find(positionItem => positionItem.name === positionValue),
    [positionValue, positions],
  );
  const selectedPositionIndex = useMemo(
    () => findIndexById(positions, selectedPosition),
    [positions, selectedPosition],
  );
  const targetPositions = useMemo(
    // No current position selected doesn't mean no valid targets — the
    // "at or above the current position" floor only applies once a position
    // is actually chosen (positionId and targetPositionId are independent).
    () =>
      selectedPositionIndex >= FIRST_INDEX
        ? positions.slice(selectedPositionIndex)
        : positions,
    [positions, selectedPositionIndex],
  );

  const selectedExpertiseId =
    findIdByName(expertises, expertiseValue) ??
    (expertiseValue === initialExpertiseValue ? initialValues?.expertiseId : undefined);
  const selectedPositionId =
    findIdByName(positions, positionValue) ??
    (positionValue === initialPositionValue ? initialValues?.positionId : undefined);
  const selectedTargetPositionId =
    findIdByName(targetPositions, targetPositionValue) ??
    (targetPositionValue === initialTargetPositionValue
      ? initialValues?.targetPositionId ?? initialValues?.positionId
      : undefined);

  const optionsExpertise = useMemo(
    () => [defaultValue, ...expertises.map(expertiseItem => expertiseItem.name)],
    [defaultValue, expertises],
  );
  const optionsPositions = useMemo(
    () => [defaultValue, ...positions.map(positionItem => positionItem.name)],
    [defaultValue, positions],
  );
  const optionsTargetPositions = useMemo(
    () => [defaultValue, ...targetPositions.map(positionItem => positionItem.name)],
    [defaultValue, targetPositions],
  );

  useEffect(() => {
    if (!positionLookup.data) return;
    setTargetPositionValue(prevTargetPositionValue => {
      const currentPosition = positions.find(
        item => item.name === positionValueRef.current,
      );
      const currentTargetPosition = positions.find(
        item => item.name === prevTargetPositionValue,
      );
      const currentPositionIndex = findIndexById(positions, currentPosition);
      const currentTargetPositionIndex = findIndexById(positions, currentTargetPosition);
      const shouldUseCurrentPosition =
        currentPosition &&
        (!currentTargetPosition || currentTargetPositionIndex < currentPositionIndex);
      return shouldUseCurrentPosition ? currentPosition.name : prevTargetPositionValue;
    });
  }, [positions, positionLookup.data]);

  const onSelectChangeExpertise = useCallback(
    (value: string) => {
      setExpertiseValue(value);
      setPositionValue(defaultValue);
      setTargetPositionValue(defaultValue);
      onExpertiseValueChange?.(value);
    },
    [defaultValue, onExpertiseValueChange],
  );

  const onSelectChangePosition = useCallback(
    (value: string) => {
      const nextPosition = positions.find(positionItem => positionItem.name === value);

      setPositionValue(value);
      setTargetPositionValue(prevTargetPositionValue => {
        const currentTargetPosition = positions.find(
          positionItem => positionItem.name === prevTargetPositionValue,
        );
        const nextPositionIndex = findIndexById(positions, nextPosition);
        const currentTargetPositionIndex = findIndexById(
          positions,
          currentTargetPosition,
        );
        const shouldUseNextPosition =
          nextPosition &&
          (!currentTargetPosition || currentTargetPositionIndex < nextPositionIndex);

        return shouldUseNextPosition ? nextPosition.name : prevTargetPositionValue;
      });
    },
    [positions],
  );

  const onSelectChangeTargetPosition = useCallback(
    (value: string) => {
      const isValidTarget =
        value === defaultValue ||
        targetPositions.some(positionItem => positionItem.name === value);

      if (isValidTarget) {
        setTargetPositionValue(value);
      }
    },
    [defaultValue, targetPositions],
  );

  return {
    expertises,
    positions,
    targetPositions,
    expertiseValue,
    positionValue,
    targetPositionValue,
    optionsExpertise,
    optionsPositions,
    optionsTargetPositions,
    selectedExpertiseId,
    selectedPosition,
    selectedPositionId,
    selectedTargetPositionId,
    isExpertisesLoading,
    isPositionsLoading,
    isAssignmentReady:
      Boolean(scope) && expertiseLookup.isReady && positionLookup.isReady,
    hasExpertisesError: expertiseLookup.hasError,
    hasPositionsError: positionLookup.hasError,
    retryExpertises: expertiseLookup.retry,
    retryPositions: positionLookup.retry,
    onSelectChangeExpertise,
    onSelectChangePosition,
    onSelectChangeTargetPosition,
  };
};

export { useExpertisePositionAssignment };
